import { useState, useEffect } from 'react';
import { X, Cookie, ChevronRight } from 'lucide-react';
import useTranslation from '../data/useTranslation';

const STORAGE_KEY = 'bpm_cookie_consent';

function CookieBanner() {
  const { t } = useTranslation();
  const c = t.cookie;
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) setVisible(true);
  }, []);

  const persist = (analyticsValue) => {
    const payload = {
      analytics: analyticsValue,
      necessary: true,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Notify same-tab listeners
    window.dispatchEvent(new Event('bpm-consent-changed'));
    setVisible(false);
    setShowCustomize(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-[#111827]/98 backdrop-blur-md border-t border-orange-500/40 shadow-2xl"
        data-testid="cookie-banner"
      >
        <div className="container mx-auto px-4 py-4 sm:px-6 sm:py-5 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Cookie className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="font-semibold text-white text-sm sm:text-base mb-1">{c.title}</p>
              <p className="text-gray-400 text-xs sm:text-sm leading-snug">{c.body}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <button
              onClick={() => setShowCustomize(true)}
              data-testid="cookie-customize-btn"
              className="px-3 py-2 text-xs sm:text-sm text-gray-300 hover:text-white border border-gray-700 hover:border-gray-500 rounded-lg transition-colors"
            >
              {c.customize}
            </button>
            <button
              onClick={() => persist(false)}
              data-testid="cookie-refuse-btn"
              className="px-3 py-2 text-xs sm:text-sm bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {c.refuse}
            </button>
            <button
              onClick={() => persist(true)}
              data-testid="cookie-accept-all-btn"
              className="px-4 py-2 text-xs sm:text-sm bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-lg transition-colors"
            >
              {c.acceptAll}
            </button>
          </div>
        </div>
      </div>

      {/* Customize Modal */}
      {showCustomize && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          data-testid="cookie-customize-modal"
        >
          <div className="w-full max-w-lg bg-[#1F2937] border border-gray-700 rounded-2xl p-6 sm:p-8 relative">
            <button
              onClick={() => setShowCustomize(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              data-testid="cookie-customize-close"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <Cookie className="w-5 h-5 text-orange-500" />
              {c.customizeTitle}
            </h3>

            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-[#111827] border border-gray-700 flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm mb-1">{c.necessary}</p>
                  <p className="text-gray-400 text-xs leading-snug">{c.necessaryDesc}</p>
                </div>
                <div className="flex-shrink-0 w-11 h-6 bg-orange-500/40 rounded-full relative" aria-disabled="true">
                  <div className="absolute right-0.5 top-0.5 w-5 h-5 bg-orange-500 rounded-full" />
                </div>
              </div>

              <label
                className="p-4 rounded-xl bg-[#111827] border border-gray-700 flex items-start justify-between gap-4 cursor-pointer hover:border-orange-500/40 transition-colors"
                data-testid="cookie-analytics-toggle"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-white text-sm mb-1">{c.analytics}</p>
                  <p className="text-gray-400 text-xs leading-snug">{c.analyticsDesc}</p>
                </div>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="flex-shrink-0 w-11 h-6 bg-gray-700 peer-checked:bg-orange-500 rounded-full relative transition-colors">
                  <div className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" style={{ transform: analytics ? 'translateX(20px)' : 'translateX(0)' }} />
                </div>
              </label>
            </div>

            <button
              onClick={() => persist(analytics)}
              data-testid="cookie-save-btn"
              className="mt-6 w-full px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {c.save}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default CookieBanner;
